import React, { Component } from "react";
import PropTypes from "prop-types";
import { Menu, Card, Divider } from "antd";
import Helmet from "react-helmet";
import { getCasesByIndustry, getCasesByEntityType, getCasesBySourceOverYears, getCasesByEntityTypeOverYears, getCasesByTPLFOverYears } from "../../services/api-maxval";
import { DonutChart, StackedBarChart, makeWidthFlexible } from "@unifiedpatents/euclid-react";
import { nestedObjectsToGroupedSeries, objectToSeries } from "@unifiedpatents/euclid-react/lib/helpers/preprocessors";
import * as Colors from "@unifiedpatents/euclid-react/lib/helpers/colors";
import { withChartData } from "../common/chart-data-hoc";
import { withChartConfig } from "../common/chart-config-hoc";
import { range as _range, toInteger as _toInteger } from "lodash";

const styles = {
  body: {
    width: "100%",
    padding: 30
  },
  gutter: {
    width: "100%",
    marginTop: 16
  }
};

class Report extends Component {
  static propTypes = {
    year: PropTypes.number.isRequired
  };

  render() {
    const { year } = this.props;
    const q = { filed_date: `${year}-01-01--${year}-12-31` };
    const qPast = { filed_date: `2010-01-01--${year}-12-31` };
    const qPastDC = { ...qPast, flag: "DC" };
    return (
      year && (
        <Card style={styles.gutter} key={year}>
          <div style={styles.gutter}>
            <h2>{`${year}: Litigations Filed`}</h2>
            {this.renderCasesBySourceOverYearsChart(qPast)}
          </div>
          <Divider />
          <div style={styles.gutter}>
            <h2>{`${year}: District Court Litigations Filed`}</h2>
            {this.renderCasesByEntityTypeOverYearsChart(qPastDC)}
          </div>
          <Divider />
          <div style={styles.gutter}>
            <h2>{`${year}: 3rd Party Financed D.C. Litigations`}</h2>
            {this.renderCasesByTPLFOverYearsChart(qPastDC)}
          </div>
          <Divider />
          <div style={styles.gutter}>
            <h2>{`${year}: Litigations by Sector`}</h2>
            {this.renderCasesByIndustryChart(q)}
          </div>
          <Divider />
          <div style={styles.gutter}>
            <h2>{`${year}: Litigations by Plaintiff Type`}</h2>
            {this.renderCasesByEntityTypeChart(q)}
          </div>
        </Card>
      )
    );
  }

  renderTooltipExtraRowToShowTotal = v => {
    const total = v.reduce((total, obj) => total + obj.value, 0);
    return (
      <span>
        <span style={{ fontSize: "125%", fontWeight: "bold" }}>TOTAL</span>
        <span style={{ float: "right", fontSize: "125%", fontWeight: "bold" }}>{total}</span>
      </span>
    );
  };

  renderCasesBySourceOverYearsChart = q => {
    const colorMap = {
      "DISTRICT COURT": Colors.blue400,
      "SUPREME COURT": Colors.blue200,
      "FEDERAL CIRCUIT": Colors.grey200,
      "FEDERAL CLAIMS": Colors.grey300,
      "INTERNATIONAL TRADE COMMISSION": Colors.grey400
    };
    const dataFormatter = data => nestedObjectsToGroupedSeries(data);
    const Chart = withChartConfig(withChartData(makeWidthFlexible(StackedBarChart), getCasesBySourceOverYears, q));
    return (
      <Chart
        dataFormatter={dataFormatter}
        colorMap={colorMap}
        legends={["DISTRICT COURT", "SUPREME COURT", "FEDERAL CLAIMS", "FEDERAL CIRCUIT", "INTERNATIONAL TRADE COMMISSION"]}
        height={400}
        tooltipExtraRow={this.renderTooltipExtraRowToShowTotal}
      />
    );
  };

  renderCasesByEntityTypeOverYearsChart = q => {
    const colorMap = {
      "OPERATING COMPANY": Colors.grey200,
      UNIVERSITY: Colors.grey300,
      "GOV/NGO/NON-PROFIT": Colors.grey400,
      "NPE (PATENT ASSERTION ENTITY)": Colors.blue400,
      "NPE (SMALL COMPANY)": Colors.blue300,
      "NPE (INDIVIDUAL)": Colors.blue200,
      UNKNOWN: Colors.grey500
    };
    const dataFormatter = data => nestedObjectsToGroupedSeries(data);
    const Chart = withChartConfig(withChartData(makeWidthFlexible(StackedBarChart), getCasesByEntityTypeOverYears, q));
    return (
      <Chart
        title="NPE v.s. Non-NPE Plaintiffs"
        dataFormatter={dataFormatter}
        colorMap={colorMap}
        legends={["OPERATING COMPANY", "UNIVERSITY", "GOV/NGO/NON-PROFIT", "NPE (PATENT ASSERTION ENTITY)", "NPE (SMALL COMPANY)", "NPE (INDIVIDUAL)", "UNKNOWN"]}
        height={400}
        tooltipExtraRow={this.renderTooltipExtraRowToShowTotal}
      />
    );
  };

  renderCasesByIndustryChart = q => {
    const Chart = withChartConfig(withChartData(makeWidthFlexible(DonutChart), getCasesByIndustry, q));
    return <Chart title="Cases by Industry" dataFormatter={objectToSeries} height={400} scrollable={false} />;
  };

  renderCasesByEntityTypeChart = q => {
    const colorMap = {
      "Operating Company": Colors.grey300,
      University: Colors.grey400,
      "Gov/NGO/Non-Profit": Colors.grey500,
      "NPE (Patent Assertion Entity)": Colors.blue400,
      "NPE (Small Company)": Colors.blue300,
      "NPE (Individual)": Colors.blue200
    };
    const Chart = withChartConfig(withChartData(makeWidthFlexible(DonutChart), getCasesByEntityType, q));
    return <Chart title="Cases by Entity Type" dataFormatter={objectToSeries} colorMap={colorMap} height={400} scrollable={false} />;
  };

  renderCasesByTPLFOverYearsChart = q => {
    const colorMap = {
      "3rd Party Financed Cases": Colors.orange400,
      "Other NPE Cases": Colors.blue200,
      "Non-NPE Cases": Colors.grey100
    };
    const dataFormatter = data => nestedObjectsToGroupedSeries(data);
    const Chart = withChartConfig(withChartData(makeWidthFlexible(StackedBarChart), getCasesByTPLFOverYears, q));
    return (
      <Chart
        dataFormatter={dataFormatter}
        colorMap={colorMap}
        legends={["3rd Party Financed Cases", "Other NPE Cases", "Non-NPE Cases"]}
        height={400}
        tooltipExtraRow={this.renderTooltipExtraRowToShowTotal}
      />
    );
  };
}

const AnnualReport = props => {
  const latestYear = new Date().getUTCFullYear() - 1;
  const year = _toInteger(props.location.query.year) || latestYear;
  const helmet = {
    title: `${year} Litigation Annual Report`,
    meta: [
      {
        name: "keywords",
        content: `patent litigation by court,patent litigation by NPE,patent litigation by industry,patent litigation by company`
      },
      {
        name: "description",
        content: `Unified’s Patent Litigation Annual Report provides a breakdown of the patent litigation filed every year. The interactive search allows users to identify numerical patterns in information like venue filing, plaintiff type, and sector.`
      }
    ]
  };
  const yearSelector = (
    <Menu
      selectedKeys={[`${year}`]}
      onClick={item =>
        props.router.push({
          pathname: "/litigation/annual-report",
          query: {
            year: item.key
          }
        })
      }
      mode="horizontal"
    >
      {_range(2015, latestYear + 2).map(y => (
        <Menu.Item key={y}>{y}</Menu.Item>
      ))}
    </Menu>
  );
  return (
    <div style={styles.body}>
      <Helmet {...helmet} />
      <h2>{"Litigation Annual Report"}</h2>
      {yearSelector}
      <Report year={year} />
    </div>
  );
};

export default AnnualReport;
